import { AlertTriangle } from 'lucide-react';

import { useUsuario } from '@/lib/auth/contexto';
import { podeNaEmpresa } from '@/lib/auth/permissoes';
import { mensagemDeErro } from '@/lib/erros';
import { formatarDocumento } from '@/lib/formato';
import { Carimbo } from '@/ui/components/carimbo';
import { Dinheiro } from '@/ui/components/dinheiro';
import { EstadoVazio } from '@/ui/components/estado-vazio';
import { GavetaDetalhe } from '@/ui/patterns/gaveta-detalhe';
import { Tabs } from '@/ui/primitives/tabs';

import { AbaArquivos } from './aba-arquivos';
import { AbaConferencias } from './aba-conferencias';
import { AbaEventos } from './aba-eventos';
import { AbaItens } from './aba-itens';
import { AbaPagamentos } from './aba-pagamentos';
import { AbaResumo } from './aba-resumo';
import { AcoesDocumento } from './acoes-documento';
import { useDocumento } from './api';
import { MODELO, SITUACAO } from './rotulos';

interface Props {
  id: string | null;
  aoFechar: () => void;
  podeBaixar: boolean;
}

export function GavetaDocumento({ id, aoFechar, podeBaixar }: Props) {
  const usuario = useUsuario();
  const consulta = useDocumento(id);
  const doc = consulta.data;
  const titulo = doc ? `${MODELO[doc.modelo]} ${String(Number(doc.numero))}` : 'Nota fiscal';
  const subtitulo = doc
    ? `${doc.contraparte.nome ?? ''} ${formatarDocumento(doc.contraparte.documento)}`
    : undefined;

  return (
    <GavetaDetalhe
      aberta={id !== null}
      aoFechar={aoFechar}
      titulo={titulo}
      subtitulo={subtitulo}
      carregando={consulta.isPending && id !== null}
      erro={
        consulta.isError ? (
          <EstadoVazio
            tom="perigo"
            icone={<AlertTriangle />}
            titulo="Não deu para abrir a nota"
            texto={mensagemDeErro(consulta.error)}
          />
        ) : undefined
      }
    >
      {doc && (
        <>
          <div className="flex items-center justify-between gap-3">
            <Carimbo tom={SITUACAO[doc.situacao].tom} className="text-sm">
              {SITUACAO[doc.situacao].rotulo}
            </Carimbo>
            <Dinheiro valor={doc.valorTotal} className="text-2xl font-semibold tracking-tight" />
          </div>
          {podeBaixar && <AcoesDocumento doc={doc} />}
          <Tabs
            rotulo="Seções da nota"
            abas={[
              { valor: 'resumo', rotulo: 'Resumo', conteudo: <AbaResumo doc={doc} /> },
              {
                valor: 'itens',
                rotulo: 'Itens e tributos',
                extra: <Contagem n={doc.itens.length} />,
                conteudo: (
                  <AbaItens
                    doc={doc}
                    podeClassificar={podeNaEmpresa(
                      usuario,
                      doc.empresaId,
                      'documentos.classificar',
                    )}
                  />
                ),
              },
              {
                valor: 'conferencias',
                rotulo: 'Conferências',
                extra: <Contagem n={doc.conferencias.length} />,
                conteudo: <AbaConferencias conferencias={doc.conferencias} />,
              },
              { valor: 'pagamentos', rotulo: 'Pagamentos', conteudo: <AbaPagamentos doc={doc} /> },
              {
                valor: 'eventos',
                rotulo: 'Eventos',
                extra: <Contagem n={doc.eventos.length} />,
                conteudo: <AbaEventos doc={doc} />,
              },
              {
                valor: 'arquivos',
                rotulo: 'Arquivos',
                conteudo: <AbaArquivos doc={doc} podeBaixar={podeBaixar} />,
              },
            ]}
          />
        </>
      )}
    </GavetaDetalhe>
  );
}

function Contagem({ n }: { n: number }) {
  return n > 0 ? (
    <span className="rounded-pilula bg-superficie-afundada px-1.5 text-xs tabular">{n}</span>
  ) : null;
}
